import { ACTION, SEARCH_DEPARTMENT_ENGLISH_NAME } from './constants';
import getPageType from './getPageType';
import isServer from './isServer';
import $window from './window';

const triggerAnalyticsEvent = (analyticsKey, analyticsData) => {
  if (!$window.digitalData) {
    return;
  }
  $window.digitalData.trigger(analyticsKey, analyticsData);
};
const getDepartmentNameById = (departmentId) => {
  // if numericDepartmentId is an empty string return "All"
  if (departmentId === '') {
    return SEARCH_DEPARTMENT_ENGLISH_NAME?.All;
  }
  const numericDepartmentId = parseInt(departmentId.replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(numericDepartmentId)) {
    return null;
  }
  return SEARCH_DEPARTMENT_ENGLISH_NAME?.[numericDepartmentId];
};

const triggerFilterAnalytics = (storeDetails, filter) => {
  // When the toggle is on and we have a store, send to dataLayer
  if (filter === `localStoreId:${storeDetails?.storeId}` && storeDetails?.storeName) {
    triggerAnalyticsEvent(`${getPageType()}_filter_applied`, {
      search_filter_applied_category: storeDetails.storeName,
      search_filter_applied_value_name: 'Same Day Delivery',
      event_name: 'filter_applied',
      tealium_event: 'filters_applied',
    });
  }
};

const triggerSearchTermAnalytics = ({
  actionType,
  algonomyPersistentData,
  completionType,
  searchDefaultViewPlace,
  departmentId,
  isInitialSearch,
  previousSearchterm,
  removeAlgonomyPersistentData,
  resultsCount,
  searchTerm,
  submitMethod,
  userInputedSearchTerm,
}) => {
  if (
    previousSearchterm === searchTerm
        || ![ACTION.SEARCHTERM, 'pageLoad', ACTION.DEPARTMENT_CHANGE].includes(actionType)
        || isServer()
        || !$window.digitalData
  ) {
    return;
  }

  const suggestionTypeMap = {
    popular: searchDefaultViewPlace ?? 'search',
    recent: searchDefaultViewPlace ?? 'search',
    suggestions: searchTerm,
    products: searchTerm,
  };

  const suggestionType = suggestionTypeMap[completionType] || null;

  const analyticsData = {
    key: 'search.search_bar_used',
    data: {
      name: 'Search bar used',
      search_bar_category: completionType ?? null,
      search_bar_suggestion: suggestionType,
      search_corrected_search_term: null,
      search_results: resultsCount,
      search_term: searchTerm,
    },
  };

  if (searchTerm !== userInputedSearchTerm && String(userInputedSearchTerm).trim() !== '') {
    analyticsData.data.search_corrected_search_term = searchTerm;
    analyticsData.data.search_term = userInputedSearchTerm;
  }

  if (!isInitialSearch) {
    analyticsData.data.data_text = 'editable search';
    analyticsData.data.data_action = submitMethod?.trigger === 'attributePill' ? 'visual text' : 'text';
    if (submitMethod?.trigger === 'attributePill') {
      analyticsData.data.search_attribute = submitMethod.attribute;
    }
  }

  if (algonomyPersistentData
    && Object.keys(algonomyPersistentData).length > 0
    && completionType === 'products'
    && isInitialSearch
  ) {
    analyticsData.data.pagefm = algonomyPersistentData?.pagefm ?? '';
    analyticsData.data.precPla = algonomyPersistentData?.precPla ?? '';
    analyticsData.data.precPos = algonomyPersistentData?.precPos ?? '';
    removeAlgonomyPersistentData();
  }
  // departmentId can be '' so we need to check for null and undefined
  if (departmentId !== null
    && departmentId !== undefined
    && actionType === ACTION.DEPARTMENT_CHANGE) {
    analyticsData.data.data_text = 'shop by';
    analyticsData.data.data_action = getDepartmentNameById(departmentId);
  }
  triggerAnalyticsEvent(analyticsData.key, analyticsData.data);
};

/**
 * Extracts the full category path and parent category ID from the breadcrumb trail.
 *
 * @param {Array} breadcrumbTrailEntryView - The breadcrumb trail array from category data.
 * @param {string|number} [parentCatIdFromLs] - The parent category ID from local storage.
 * @returns {Object} An object containing the full category path and parent category ID.
 */
const prepareCategoryAnalytics = (breadcrumbTrailEntryView, parentCatIdFromLs) => {
  const parentCatIdFromBreadcrumb = (breadcrumbTrailEntryView?.length > 1)
    ? breadcrumbTrailEntryView[breadcrumbTrailEntryView.length - 2]?.value : null;
  if (!Array.isArray(breadcrumbTrailEntryView) || breadcrumbTrailEntryView.length === 0) {
    return null;
  }
  const categoryPath = breadcrumbTrailEntryView.reduce((acc, curr) => {
    if (acc) {
      return `${acc} > ${curr.label}`;
    }
    return curr.label;
  }, '');

  const parentCatId = parentCatIdFromLs || parentCatIdFromBreadcrumb || null;

  return { categoryPath, parentCatId };
};

/**
 * Triggers category analytics when a category page is viewed.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.actionType - The current action (should be 'CATEGORY_CHANGE').
 * @param {string|number} params.categoryId - The ID of the current category.
 * @param {string} params.categoryName - The name/path of the current category.
 * @param {boolean} params.isSsr - Whether the page is being rendered on the server side.
 * @param {string|number} [params.parentCatId] - The ID of the parent category, if available.
 */
const triggerCategoryAnalytics = ({
  actionType,
  categoryId,
  categoryName,
  isSsr,
  parentCatId,
}) => {
  if (actionType !== ACTION.CATEGORY_CHANGE || isSsr || !$window.digitalData) {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const pageFindingMethod = searchParams.get('pagefm');
  const dataText = searchParams.get('dataText');
  const dataAction = searchParams.get('dataAction');
  const productRecommendationPlacement = searchParams.get('precPla');
  const productRecommendationPosition = searchParams.get('precPos');

  const analyticsData = {
    key: 'category',
    data: {
      tealium_event: 'category',
      event_name: 'category',
      ...(categoryId && { category_id: categoryId }),
      ...(categoryName && { category_name: categoryName }),
      ...(parentCatId && { parent_category_id: parentCatId }),
      ...(pageFindingMethod && { page_finding_method: pageFindingMethod }),
      ...(dataText && { data_text: dataText }),
      ...(dataAction && { data_action: dataAction }),
      ...(productRecommendationPlacement && {
        product_recommendation_placement: productRecommendationPlacement,
      }),
      ...(productRecommendationPosition && {
        product_recommendation_position: productRecommendationPosition,
      }),
    },
  };

  triggerAnalyticsEvent(analyticsData.key, analyticsData.data);
};

const triggerFacetAnalytics = () => {
// TO DO: Implement facet analytics
};

export {
  prepareCategoryAnalytics,
  triggerCategoryAnalytics,
  triggerFilterAnalytics,
  triggerSearchTermAnalytics,
  triggerFacetAnalytics,
};
